import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";

interface ManualActivationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tenantId: string;
  tenantName: string;
  onSuccess: () => void;
}

export function ManualActivationModal({
  open,
  onOpenChange,
  tenantId,
  tenantName,
  onSuccess,
}: ManualActivationModalProps) {
  const [reason, setReason] = useState<string>("dinheiro");
  const [observations, setObservations] = useState("");

  const activateMutation = useMutation({
    mutationFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Usuário não autenticado");

      // Calcular próxima data de cobrança (30 dias)
      const nextBillingDate = new Date();
      nextBillingDate.setDate(nextBillingDate.getDate() + 30);

      // Determinar subscription_type baseado no motivo
      let subscriptionType = 'regular';
      if (reason === 'cortesia') {
        subscriptionType = 'cortesia';
      } else if (reason === 'testers') {
        subscriptionType = 'testers';
      }

      // Atualizar tenant
      const { error: tenantError } = await supabase
        .from("tenants")
        .update({
          subscription_status: "active",
          subscription_plan_id: "plus",
          subscription_type: subscriptionType,
          subscription_payment_method: "manual",
          subscription_start_date: new Date().toISOString(),
          subscription_next_billing_date: nextBillingDate.toISOString(),
          subscription_last_payment_date: new Date().toISOString(),
        })
        .eq("id", tenantId);

      if (tenantError) throw tenantError;

      // Determinar payment_type baseado no motivo
      let paymentType = 'regular';
      if (reason === 'cortesia') {
        paymentType = 'cortesia';
      } else if (reason === 'testers') {
        paymentType = 'testers';
      } else if (reason === 'promotion') {
        paymentType = 'promotion';
      }

      // Registrar transação
      const { error: transactionError } = await supabase
        .from("subscription_transactions")
        .insert({
          tenant_id: tenantId,
          transaction_type: "manual_activation",
          payment_method: paymentType === 'regular' ? "manual" : paymentType,
          payment_type: paymentType,
          subscription_plan_id: "plus",
          status: "approved",
          payment_date: new Date().toISOString(),
          created_by: user.id,
          metadata: {
            reason,
            observations,
            activated_by_name: user.email,
            subscription_type: subscriptionType,
            payment_type: paymentType,
          },
        });

      if (transactionError) throw transactionError;
    },
    onSuccess: () => {
      toast.success("Plano Plus ativado manualmente com sucesso!");
      setReason("dinheiro");
      setObservations("");
      onSuccess();
    },
    onError: (error: any) => {
      toast.error("Erro ao ativar plano: " + error.message);
    },
  });

  const handleActivate = () => {
    activateMutation.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md p-0 overflow-hidden">
        <div className="flex max-h-[80vh] flex-col">
          <DialogHeader className="sticky top-0 z-10 bg-background px-6 py-4 border-b">
            <DialogTitle>Ativar Plano Plus Manualmente</DialogTitle>
            <DialogDescription>
              Ativar o plano Plus para <strong>{tenantName}</strong> sem processamento de pagamento
            </DialogDescription>
          </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          <div className="space-y-2">
            <Label>Motivo da Ativação Manual</Label>
            <RadioGroup value={reason} onValueChange={setReason}>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="dinheiro" id="dinheiro" />
                <Label htmlFor="dinheiro" className="font-normal cursor-pointer">
                  Pagamento em Dinheiro
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="cortesia" id="cortesia" />
                <Label htmlFor="cortesia" className="font-normal cursor-pointer">
                  Cortesia (sem cobrança)
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="testers" id="testers" />
                <Label htmlFor="testers" className="font-normal cursor-pointer">
                  Beta Tester (sem cobrança)
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="promotion" id="promotion" />
                <Label htmlFor="promotion" className="font-normal cursor-pointer">
                  Promoção (sem cobrança)
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="outro" id="outro" />
                <Label htmlFor="outro" className="font-normal cursor-pointer">
                  Outro
                </Label>
              </div>
            </RadioGroup>
          </div>

          <div className="space-y-2">
            <Label htmlFor="observations">Observações (opcional)</Label>
            <Textarea
              id="observations"
              value={observations}
              onChange={(e) => setObservations(e.target.value)}
              placeholder="Adicione observações adicionais..."
              rows={4}
            />
          </div>

          <div className="bg-muted p-4 rounded-lg space-y-2">
            <p className="text-sm font-medium">Esta ação irá:</p>
            <ul className="text-sm space-y-1 list-disc list-inside text-muted-foreground">
              <li>Ativar o Plano Plus imediatamente</li>
              <li>Definir status como "active"</li>
              <li>Definir próxima cobrança para 30 dias</li>
              <li>Registrar transação manual no histórico</li>
            </ul>
          </div>
        </div>

        <div className="bg-background px-6 py-4 border-t flex justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={activateMutation.isPending}
          >
            Cancelar
          </Button>
          <Button onClick={handleActivate} disabled={activateMutation.isPending}>
            {activateMutation.isPending ? "Ativando..." : "Ativar Plano Plus"}
          </Button>
        </div>
      </div>
      </DialogContent>
    </Dialog>
  );
}
